/**
 * Chamada às rotas de transcrição do webhook COM o login da sessão.
 *
 * Desde 23/09/2026 /api/transcribe e /api/transcribe/structure exigem equipe
 * clínica. Em produção (24 e 28/09) a Doutora viu "Transcrição falhou" no fim
 * de gravações que o servidor transcreveu normalmente: a chamada rápida do
 * navegador saiu sem sessão válida e levou 401. O áudio e o registro já estavam
 * salvos, e o servidor transcreve toda gravação do portal sozinho (gatilho da
 * migration 074 ao inserir em plaud_recordings).
 *
 * Por isso aqui:
 *  1. sem token na sessão, tenta renovar a sessão uma vez;
 *  2. resposta 401, renova e tenta de novo uma vez;
 *  3. se continuar sem sessão, lança SemSessao — quem chamou NÃO marca falha e
 *     avisa que o servidor está transcrevendo.
 */
import { supabase, getAccessToken } from './supabaseClient';

/** A sessão não existe ou não pôde ser renovada: a transcrição fica com o servidor. */
export class SemSessao extends Error {
  constructor() {
    super('sessão expirada');
    this.name = 'SemSessao';
  }
}

async function renovarSessao(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.refreshSession();
    return data.session?.access_token || null;
  } catch {
    return null;
  }
}

async function tokenDaSessao(): Promise<string | null> {
  return (await getAccessToken()) || (await renovarSessao());
}

/**
 * fetch com `Authorization: Bearer <token>`. Renova a sessão uma vez se faltar
 * token ou se o webhook responder 401. Continua sem sessão → SemSessao.
 * O corpo pode ser FormData ou string (os dois podem ser reenviados).
 */
export async function fetchTranscricao(url: string, init: RequestInit, extraHeaders: Record<string, string> = {}): Promise<Response> {
  let token = await tokenDaSessao();
  if (!token) throw new SemSessao();
  let res = await fetch(url, { ...init, headers: { ...extraHeaders, Authorization: `Bearer ${token}` } });
  if (res.status !== 401) return res;

  token = await renovarSessao();
  if (!token) throw new SemSessao();
  res = await fetch(url, { ...init, headers: { ...extraHeaders, Authorization: `Bearer ${token}` } });
  if (res.status === 401) throw new SemSessao();
  return res;
}
